import type { Task } from '../models/Task';

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseDateOnlyAsLocalDate = (value: string): Date | null => {
  const match = DATE_ONLY_PATTERN.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
};

export const toDateOrNull = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const localDate = parseDateOnlyAsLocalDate(trimmed);
    if (localDate) return localDate;

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const buildTaskGithubPayload = (
  data: Record<string, unknown>,
  clientRepoId?: number | null
): Task['github'] | undefined => {
  const repoId = asFiniteNumber(data.githubRepoId) ?? (clientRepoId != null ? Number(clientRepoId) : undefined);
  const issueId = asFiniteNumber(data.githubIssueId);
  const issueNumber = asFiniteNumber(data.githubIssueNumber);
  const issueTitle = asNonEmptyString(data.githubIssueTitle);
  const issueUrl = asNonEmptyString(data.githubIssueUrl);
  const branchName = asNonEmptyString(data.githubBranchName);
  const branchUrl = asNonEmptyString(data.githubBranchUrl);

  const hasAnyValue =
    repoId != null ||
    issueId != null ||
    issueNumber != null ||
    issueTitle != null ||
    issueUrl != null ||
    branchName != null ||
    branchUrl != null;

  if (!hasAnyValue) return undefined;

  return {
    repoId,
    issueId,
    issueNumber,
    issueTitle,
    issueUrl,
    branchName,
    branchUrl,
  };
};

export const buildTaskJiraPayload = (data: Record<string, unknown>): Task['jira'] | undefined => {
  const issueId = asNonEmptyString(data.jiraIssueId);
  const issueKey = asNonEmptyString(data.jiraIssueKey);
  const issueUrl = asNonEmptyString(data.jiraIssueUrl);

  if (!issueId && !issueKey && !issueUrl) {
    return undefined;
  }

  return {
    issueId,
    issueKey,
    issueUrl,
  };
};
